import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * Custom Select dropdown with brand styling.
 * 드롭다운을 document.body에 portal로 렌더링하여 overflow:hidden 컨테이너(모달 등) 내에서도 클리핑 없이 동작.
 *
 * @param {string} value - current selected value
 * @param {function} onChange - callback with selected value string
 * @param {string} placeholder - placeholder text when no value
 * @param {Array} options - flat [{value, label}] or grouped [{label, options: [{value, label}]}]
 * @param {string} className - additional wrapper class
 */
export default function Select({ value, onChange, placeholder, options = [], className = '', variant = 'default', size = 'md' }) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t('common.select');
  const [open, setOpen] = useState(false);
  const [dropStyle, setDropStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Detect grouped vs flat
  const isGrouped = options.length > 0 && Array.isArray(options[0]?.options);

  // Find display label for current value
  // - 옵션에 shortLabel이 있으면 trigger에는 짧은 라벨, 드롭다운엔 전체 label 사용
  //   (예: 국가 다이얼 코드 — trigger="+82", dropdown="+82 대한민국")
  const getLabel = () => {
    if (!value) return null;
    if (isGrouped) {
      for (const group of options) {
        const found = group.options.find((o) => o.value === value);
        if (found) return found.shortLabel || found.label;
      }
    } else {
      const found = options.find((o) => o.value === value);
      if (found) return found.shortLabel || found.label;
    }
    return null;
  };

  const displayLabel = getLabel();

  // 트리거 위치 계산 후 열기. BugHerd #53:
  //   1. 아래 공간 충분하면 아래로 펼침 (기본).
  //   2. 아래 공간 부족 + 위 공간 충분 → 위로 펼침 (모달 안에서도 동작).
  //   3. 양쪽 다 부족 → 더 큰 쪽으로 펼치되 maxHeight 동적 축소.
  // SearchFilterSheet 같이 fixed 모달 안에서는 페이지 scroll 이 안 먹어
  // 이전의 window.scrollBy 방식은 무력함 → 위로 펼침이 유일한 해결.
  const handleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const DROPDOWN_MAX_H = 280;
    const GAP = 6;
    const SAFE_MARGIN = 16;
    const spaceBelow = window.innerHeight - r.bottom - GAP - SAFE_MARGIN;
    const spaceAbove = r.top - GAP - SAFE_MARGIN;

    if (spaceBelow >= DROPDOWN_MAX_H) {
      // 아래 충분 → 아래로 (top anchor)
      setDropStyle({ top: r.bottom + GAP, left: r.left, width: r.width, maxHeight: DROPDOWN_MAX_H });
    } else if (spaceAbove >= DROPDOWN_MAX_H) {
      // 아래 부족 + 위 충분 → 위로. bottom anchor 사용해 실제 콘텐츠 높이에 관계없이
      // 드롭다운 bottom 이 input top 바로 위 (GAP) 에 붙도록 함. 이전엔 top 절대값에
      // MAX_H 고정으로 콘텐츠 짧을 때 input 과 dropdown 사이 빈공간 발생.
      setDropStyle({ bottom: window.innerHeight - r.top + GAP, left: r.left, width: r.width, maxHeight: DROPDOWN_MAX_H });
    } else if (spaceAbove > spaceBelow) {
      // 양쪽 다 부족하지만 위 공간이 더 큼 → 위로 + 축소. bottom anchor.
      const maxH = Math.max(120, spaceAbove);
      setDropStyle({ bottom: window.innerHeight - r.top + GAP, left: r.left, width: r.width, maxHeight: maxH });
    } else {
      // 양쪽 다 부족 + 아래 공간이 더 큼 → 아래 + 축소
      const maxH = Math.max(120, spaceBelow);
      setDropStyle({ top: r.bottom + GAP, left: r.left, width: r.width, maxHeight: maxH });
    }
    setOpen(true);
  };

  // 외부 클릭 시 닫기 (portal 포함)
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // 스크롤/리사이즈 시 닫기 (드롭다운 내부 스크롤은 제외)
  useEffect(() => {
    if (!open) return;
    const handleScroll = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // Escape 키
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  const renderOption = (opt) => {
    const isSelected = opt.value === value;
    return (
      <button
        key={opt.value}
        type="button"
        onClick={() => handleSelect(opt.value)}
        className={`w-full text-left px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
          isSelected
            ? 'bg-orange-50 text-orange-500 font-semibold'
            : 'text-mono-900 hover:bg-orange-50/50'
        }`}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="truncate">{opt.label}</span>
          {isSelected && <Icon name="check" size={16} className="text-orange-500 flex-shrink-0" />}
        </span>
      </button>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        className={`w-full text-left transition font-medium truncate whitespace-nowrap ${
          size === 'sm' ? 'h-8 px-3 pr-8 text-xs' : 'h-12 px-4 pr-10 text-[15px]'
        } ${
          variant === 'borderless'
            ? 'bg-transparent border-0'
            : `bg-white border rounded-lg ${open ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-mono-200 hover:border-mono-300'}`
        } ${displayLabel ? 'text-mono-900' : 'text-mono-500'}`}
      >
        {displayLabel || resolvedPlaceholder}
      </button>
      <Icon
        name="chevron-down"
        size={size === 'sm' ? 14 : 16}
        className={`absolute ${size === 'sm' ? 'right-2.5' : 'right-4'} top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
          open ? 'rotate-180 text-orange-500' : 'text-mono-400'
        }`}
      />

      {/* Dropdown — portal로 body에 렌더링하여 overflow 컨테이너 클리핑 방지.
          #53: maxHeight 는 handleOpen 에서 동적 계산 (위/아래 공간에 맞춰 자동). */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropStyle.top, bottom: dropStyle.bottom, left: dropStyle.left, minWidth: dropStyle.width, maxWidth: 'min(90vw, 320px)', zIndex: 9999 }}
          className="bg-white border border-mono-200 rounded-xl shadow-lg overflow-hidden animate-dropdown"
        >
          <div
            className="overflow-y-auto overscroll-contain py-1 custom-scrollbar"
            style={{ maxHeight: dropStyle.maxHeight || 280 }}
          >
            {isGrouped
              ? options.map((group) => (
                  <div key={group.label}>
                    <div className="px-4 py-2 text-2xs font-bold text-mono-400 uppercase tracking-wider bg-mono-50/60 sticky top-0">
                      {group.label}
                    </div>
                    {group.options.map(renderOption)}
                  </div>
                ))
              : options.map(renderOption)}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
